import { s } from '@/theme/tokens';
import { Stack, Text } from '@/ui';
import { Box, DateTimePicker, TextField } from '@/ui/primitives';
import { DurationSpinnerField } from './DurationSpinnerField';
import { editDialogInputBackgroundColor } from './EntityEditDialog';
import type { DurationInputMode, DurationParts } from './DetailEditDurationFields.utils';
import type { Dayjs } from 'dayjs';
import type { ReactElement } from 'react';

const durationInputGridSx = {
  display: 'grid',
  gap: s('md'),
  gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
} as const;

const timestampInputWidthPx = 200;
const timestampInputSx = {
  width: `${timestampInputWidthPx}px`,
  maxWidth: '100%',
} as const;

type DetailEditTimingFieldsProps = {
  timestampLabel: string;
  timestampValue: Dayjs | null;
  onTimestampChange: (nextValue: Dayjs | null) => void;
  showSecondaryTimestamp: boolean;
  secondaryTimestampLabel: string;
  secondaryTimestampValue: Dayjs | null;
  onSecondaryTimestampChange: (nextValue: Dayjs | null) => void;
  durationInputMode: DurationInputMode;
  durationLabel: string;
  durationValue: string;
  onDurationValueChange: (nextValue: string) => void;
  durationParts: DurationParts;
  onDurationPartsChange: (nextValue: DurationParts) => void;
  hasInvalidDuration: boolean;
};

export const DetailEditTimingFields = ({
  timestampLabel,
  timestampValue,
  onTimestampChange,
  showSecondaryTimestamp,
  secondaryTimestampLabel,
  secondaryTimestampValue,
  onSecondaryTimestampChange,
  durationInputMode,
  durationLabel,
  durationValue,
  onDurationValueChange,
  durationParts,
  onDurationPartsChange,
  hasInvalidDuration,
}: DetailEditTimingFieldsProps): ReactElement => (
  <>
    <DateTimePicker
      label={timestampLabel}
      value={timestampValue}
      onChange={onTimestampChange}
      fieldStyle="settings"
      inputBackgroundColor={editDialogInputBackgroundColor}
      textFieldSx={timestampInputSx}
    />
    {showSecondaryTimestamp ? (
      <DateTimePicker
        label={secondaryTimestampLabel}
        value={secondaryTimestampValue}
        onChange={onSecondaryTimestampChange}
        fieldStyle="settings"
        inputBackgroundColor={editDialogInputBackgroundColor}
        textFieldSx={timestampInputSx}
      />
    ) : null}
    {durationInputMode === 'timeParts' ? (
      <Stack gap="sm">
        <Text role="body">{durationLabel}</Text>
        <Box sx={durationInputGridSx}>
          <DurationSpinnerField
            label="Hours"
            value={durationParts.hours}
            min={0}
            error={hasInvalidDuration}
            onChange={(nextValue) => {
              onDurationPartsChange({ ...durationParts, hours: nextValue });
            }}
          />
          <DurationSpinnerField
            label="Minutes"
            value={durationParts.minutes}
            min={0}
            max={59}
            error={hasInvalidDuration}
            onChange={(nextValue) => {
              onDurationPartsChange({ ...durationParts, minutes: nextValue });
            }}
          />
          <DurationSpinnerField
            label="Seconds"
            value={durationParts.seconds}
            min={0}
            max={59}
            error={hasInvalidDuration}
            onChange={(nextValue) => {
              onDurationPartsChange({ ...durationParts, seconds: nextValue });
            }}
          />
          <DurationSpinnerField
            label="Milliseconds"
            value={durationParts.milliseconds}
            min={0}
            max={999}
            error={hasInvalidDuration}
            onChange={(nextValue) => {
              onDurationPartsChange({ ...durationParts, milliseconds: nextValue });
            }}
          />
        </Box>
        <Text role="captionSmall" color={hasInvalidDuration ? 'error' : 'textSecondary'}>
          {hasInvalidDuration
            ? 'Enter whole-number values. Minutes and seconds must be 0-59, milliseconds 0-999.'
            : ' '}
        </Text>
      </Stack>
    ) : (
      <TextField
        label={durationLabel}
        type="number"
        value={durationValue}
        onChange={(event) => {
          onDurationValueChange(event.target.value);
        }}
        error={hasInvalidDuration}
        helperText={hasInvalidDuration ? 'Duration must be a non-negative number.' : ' '}
        fieldStyle="settings"
        inputBackgroundColor={editDialogInputBackgroundColor}
      />
    )}
  </>
);
